import type { User } from '../modules/platform/types';
import { ROUTES, USER_ROLES } from './constants';

/** Query keys for LMS launch JWT (priority: token, assertion_jwt, assertion). */
export const LMS_ASSERTION_PARAM_KEYS = ['token', 'assertion_jwt', 'assertion'] as const;

/**
 * Extract assertion JWT from URL search params only (never from hash or logs).
 * Primary: /lms/launch?token=... ; legacy: assertion_jwt, assertion
 */
export function extractAssertionJwt(searchParams: URLSearchParams): string | null {
  for (const key of LMS_ASSERTION_PARAM_KEYS) {
    const raw = searchParams.get(key);
    if (raw && raw.trim()) {
      return raw.trim();
    }
  }
  return null;
}

/**
 * Prevent open redirects: only same-origin relative paths.
 */
export function resolveSafeReturnUrl(returnUrl: string | null | undefined, fallback: string): string {
  if (!returnUrl || !returnUrl.trim()) {
    return fallback;
  }
  const trimmed = returnUrl.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  const lower = trimmed.toLowerCase();
  if (lower.includes('://') || lower.startsWith('javascript:') || lower.startsWith('data:')) {
    return fallback;
  }
  try {
    const parsed = new URL(trimmed, 'https://localhost');
    if (parsed.origin !== 'https://localhost') {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function getDefaultRouteForUser(user: User | null): string {
  if (!user) return ROUTES.LOGIN;
  if (user.roles.includes(USER_ROLES.DEV_TEAM)) return ROUTES.DEV_DASHBOARD;
  if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) return ROUTES.ADMIN_DASHBOARD;
  if (user.roles.includes(USER_ROLES.TEACHER)) return ROUTES.TEACHER_DASHBOARD;
  if (user.roles.includes(USER_ROLES.STUDENT)) return ROUTES.STUDENT_DASHBOARD;
  if (user.roles.includes(USER_ROLES.PARENT)) return ROUTES.PARENT_DASHBOARD;
  return ROUTES.LOGIN;
}

/**
 * iframe-safe navigation: use in-app router paths only (no window.top unless same app).
 */
export function shouldUseFullPageNavigation(_targetPath: string): boolean {
  return false;
}
